import { useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ExternalLayer } from 'hooks/externalLayer/types';
import { useUpdateExternalLayerMutation } from 'hooks/externalLayer/useUpdateExternalLayerMutation';
import {
  FEED_UNIT_OPTIONS,
  formatUpdatedAgo,
  parseFeedUri,
  withFeedUnits,
  type FeedUnits,
} from 'components/MapPage/CorporateViewTabs/EventsTab/externalFeeds';
import {
  refreshFeedLayer,
  useFeedLayerStatus,
} from './arcgis-integration/feedLayer';

function useMinuteTick(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

/** Refresh, last-updated time and units for a data feed (`feed://`) layer. */
export function FeedLayerControls({
  layer,
}: {
  layer: Pick<ExternalLayer, 'id' | 'serviceUrl' | 'aorIds'>;
}) {
  const parsed = parseFeedUri(layer.serviceUrl);
  const status = useFeedLayerStatus(String(layer.id));
  const updateLayer = useUpdateExternalLayerMutation();
  const now = useMinuteTick();
  if (!parsed || !layer.serviceUrl) return null;

  const refresh = () => refreshFeedLayer(String(layer.id)).catch(() => {});
  const busy = status.refreshing || updateLayer.isPending;

  const changeUnits = async (units: FeedUnits) => {
    if (units === parsed.units) return;
    await updateLayer.mutateAsync({
      externalLayerId: layer.id,
      serviceUrl: withFeedUnits(layer.serviceUrl!, units),
      aorIds: layer.aorIds,
    });
    await refresh();
  };

  const updated = formatUpdatedAgo(status.fetchedAt, now);

  return (
    <div className="dark tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:text-foreground">
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="tw:h-7 tw:gap-1.5 tw:px-2 tw:text-xs"
        disabled={busy}
        onClick={() => void refresh()}
        aria-label="Refresh forecast"
      >
        {status.refreshing ? (
          <Loader2 className="tw:size-3.5 tw:animate-spin" />
        ) : (
          <RefreshCw className="tw:size-3.5" />
        )}
        Refresh
      </Button>
      <Select
        value={parsed.units}
        onValueChange={(next) => void changeUnits(next as FeedUnits)}
        disabled={busy}
      >
        <SelectTrigger
          size="sm"
          className="tw:h-7 tw:w-auto tw:text-xs"
          aria-label="Units"
        >
          <SelectValue>
            {parsed.units === 'imperial' ? 'Imperial' : 'Metric'}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="dark">
          {FEED_UNIT_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="tw:text-xs tw:text-muted-foreground">
        {updated ?? 'Turn the layer on to load the forecast.'}
      </span>
      {status.error ? (
        <p className="tw:m-0 tw:w-full tw:text-xs tw:text-destructive">
          {status.error}
        </p>
      ) : null}
    </div>
  );
}
